/**
 * ⚠ CONTENIDO CLÍNICO — traducción del contenido en inglés. Requiere
 * revisión clínica del equipo de lactancia de FFP antes de publicarse.
 */
import type { content as contentEn } from '../en/content';

export const content: typeof contentEn = {
  congrats: {
    title: '¡Felicidades!',
    titleWithName: (name: string) => `¡Felicidades! ¡Ya llegó ${name}!`,
    body: 'Hizo algo maravilloso. Estos primeros días son para descansar, alimentar al bebé y conocerse. Estamos con usted en todo el camino.',
    partnerBody: '¡Llegó el bebé! Su apoyo en estas primeras semanas hace una diferencia real para mamá y bebé.',
  },

  checkup: {
    title: 'Programe el chequeo del recién nacido',
    body: 'Su pediatra debe ver al bebé entre los 3 y 5 días de vida para revisar peso, alimentación e ictericia. Reservar ahora asegura que la visita caiga en la ventana correcta.',
    partnerBody: 'Una de las cosas más útiles que puede hacer ahora mismo: asegurarse de que el chequeo de los 3 a 5 días esté en el calendario.',
    scheduleOnline: 'Programar en línea',
    callToSchedule: 'Llamar para programar',
    markScheduled: 'Ya lo reservé',
    scheduledFor: (date: string) => `Cita reservada para el ${date}`,
    whenIsIt: '¿Cuándo es la visita?',
  },

  visits: {
    newborn: {
      name: 'Chequeo del recién nacido',
      window: 'De 3 a 5 días de vida',
      desc: 'Revisión de peso, alimentación e ictericia. Esta es la cita más importante para reservar de inmediato.',
    },
    lactation: {
      name: 'Seguimiento de lactancia',
      window: 'Día 4 a 7, cuando baje la leche',
      desc: 'Una IBCLC observa una toma completa, revisa el agarre y la transferencia de leche, y usted se va con un plan.',
    },
    twoWeek: {
      name: 'Chequeo de las dos semanas',
      window: 'Alrededor de las 2 semanas',
      desc: 'Control de peso. La mayoría de los bebés ya recuperaron su peso de nacimiento.',
    },
    oneMonth: {
      name: 'Visita del primer mes',
      window: 'Alrededor de 1 mes',
      desc: 'Visita de niño sano de rutina.',
    },
  },

  stages: {
    colostrum: {
      name: 'Días de calostro',
      desc: 'Los volúmenes pequeños son normales. El calostro se mide en cucharaditas, no en onzas, y es exactamente lo que su bebé necesita ahora.',
    },
    transitional: {
      name: 'La leche está bajando',
      desc: 'La leche suele bajar entre el día 3 y 5. Las tomas en racimo son normales y ayudan a que baje. Una cesárea, un parto largo, una pérdida importante de sangre o la separación del bebé pueden añadir un día o dos.',
    },
    milkIn: {
      name: 'La leche ya bajó',
      desc: 'Espere aproximadamente 1 a 3 oz por toma y de 8 a 12 tomas cada 24 horas. Los pañales mojados deben llegar a 6 o más al día.',
    },
  },

  expectations: {
    title: 'Qué esperar hoy',
    feeds: (min: number, max: number) => `${min} a ${max} tomas por 24 horas`,
    wets: (min: number) => `Al menos ${min} ${min === 1 ? 'pañal mojado' : 'pañales mojados'}`,
    dirties: (min: number) => `Al menos ${min} ${min === 1 ? 'evacuación' : 'evacuaciones'}`,
    intake: (range: string) => `Aproximadamente ${range} por toma`,
    wakeToFeed:
      'Un bebé dormilón y tranquilo también necesita comer. En las primeras dos semanas, despierte a su bebé para comer si han pasado 3 horas de día o 4 horas de noche.',
    weightNote:
      'Una pérdida de peso de hasta el 7% del peso de nacimiento está dentro de lo normal. Los bebés suelen recuperar su peso de nacimiento a las 2 semanas.',
  },

  whenToCall: {
    title: 'Cuándo comunicarse',
    lactationLine: {
      title: 'Llame o envíe un texto al equipo de lactancia',
      items: [
        'Agarre doloroso, o pezones agrietados o sangrando',
        'Congestión mamaria que no mejora',
        'Preocupaciones por la producción de leche, poca o demasiada',
        'Preguntas sobre el extractor o el tamaño de los embudos (copas) del extractor',
      ],
    },
    office: {
      title: 'Llame al consultorio',
      items: [
        'Menos pañales mojados o sucios de lo esperado para el día',
        'Menos de 8 tomas en 24 horas, o un bebé demasiado dormido para despertar a comer',
        'Piel u ojos que se ven más amarillos',
        'Fiebre, enrojecimiento o una zona dura y dolorosa en el seno (posible mastitis)',
        'Sentirse triste, ansiosa o abrumada de forma persistente',
      ],
    },
    urgent: {
      title: 'Busque atención de inmediato',
      items: [
        'Temperatura rectal de 100.4°F (38°C) o más en un bebé menor de 2 meses',
        'Ningún pañal mojado en 24 horas, o señales de deshidratación',
        'El bebé está flácido, muy difícil de despertar, o respira con dificultad',
        'Labios o cara azulados o grises',
      ],
    },
  },

  mood: {
    title: 'Su estado de ánimo perinatal',
    normal:
      'Momentos de llanto, cansancio y agobio en las primeras dos semanas ("baby blues") son comunes y suelen pasar.',
    concerning:
      'Si la tristeza o la ansiedad es intensa, dura más de dos semanas o le impide dormir cuando podría, díganos. La depresión y la ansiedad posparto son comunes y tratables, y podemos ayudar.',
    crisis:
      'Si tiene pensamientos de hacerse daño a usted o a su bebé, llame o envíe un texto al 988 (Línea de Prevención del Suicidio y Crisis) ahora. También puede llamar a Postpartum Support International al 1-800-944-4773.',
    partnerNote:
      'Parejas: ustedes conocen cómo es ella normalmente. Si mamá no parece ella misma por más de dos semanas, ayúdela con cariño a buscar apoyo. Las parejas también pueden tener depresión posparto.',
  },

  partnerTips: {
    title: 'Formas de apoyar a mamá hoy',
    intro:
      'La recuperación y la producción de leche exigen todo de mamá. Estas pequeñas tareas hacen una gran diferencia.',
    tips: [
      {
        title: 'Lleve al bebé a las tomas',
        body: 'Especialmente de noche y después de una cesárea. Cambie el pañal, lleve el bebé a mamá y encárguese de sacar los gases después.',
      },
      {
        title: 'Proteja el sueño y las tomas',
        body: 'Cuide sus siestas. Mantenga las tomas tranquilas, sin prisa y sin interrupciones.',
      },
      {
        title: 'Lleve el registro de pañales',
        body: 'Hágase cargo del registro de pañales en esta app. Los conteos de pañales mojados y sucios son la mejor señal de que el bebé está comiendo suficiente.',
      },
      {
        title: 'Agua y comida, siempre',
        body: 'Amamantar da mucha sed. Rellene su agua cada vez que se siente a dar pecho y tenga cerca comida fácil de comer con una mano.',
      },
      {
        title: 'Encárguese de las visitas',
        body: 'Sea el guardián. Visitas cortas, nada de llegadas sin avisar, y siempre está bien decir "hoy no".',
      },
      {
        title: 'Tenga su tiempo piel con piel',
        body: 'El contacto piel con piel calma al bebé, estabiliza su temperatura y respiración, y también es su momento de conexión.',
      },
      {
        title: 'Reserve las citas',
        body: 'Asegúrese de que el chequeo del recién nacido (3 a 5 días) y el seguimiento de lactancia estén programados, y planee estar presente.',
      },
      {
        title: 'Esté atento a su estado de ánimo',
        body: 'Los "baby blues" son comunes en las primeras dos semanas. Si mamá no parece ella misma después de eso, ayúdela a llamar al consultorio.',
      },
    ],
  },

  prenatal: {
    title: 'Antes de que llegue el bebé',
    countdown: (days: number) =>
      days === 1 ? 'Falta 1 día para su fecha probable de parto' : `Faltan ${days} días para su fecha probable de parto`,
    pastDue: '¡En cualquier momento!',
    intro:
      'Esta app cobra vida cuando nace su bebé. Mientras tanto, así puede prepararse.',
    checklist: [
      'Asista a su consulta prenatal de lactancia',
      'Ordene su extractor de leche a través del seguro',
      'Pregunte sobre la recolección de calostro después de las 37 semanas (consulte primero con su obstetra o partera)',
      'Planee el contacto piel con piel y una primera toma dentro de la primera hora',
      'Defina sus metas de alimentación; la alimentación combinada también es válida',
      'Guarde la línea de lactancia en los contactos de su teléfono',
    ],
    colostrumNote:
      'La extracción manual de calostro generalmente solo se aconseja después de las 37 semanas y tras consultar con su obstetra o partera, ya que puede provocar contracciones y no se recomienda en algunos embarazos.',
    babyIsHere: '¡El bebé ya está aquí!',
  },
};
